(() => {
  window.Express = window.Express || {};

  const attachedRoots = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  function getScrollBehavior(behavior) {
    if (behavior) return behavior;
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function getSettings(options = {}) {
    return {
      activeSelector: options.activeSelector || '.epb__navitem.is-active',
      containerSelector: options.containerSelector || '.epb__nav',
      controlSelector: options.controlSelector || '[data-nav-scroll-dir]',
      directionAttribute: options.directionAttribute || 'navScrollDir',
      itemSelector: options.itemSelector || '[data-nav]',
      trackSelector: options.trackSelector || '.epb__navlist',
    };
  }

  function getParts(root, options = {}) {
    const settings = getSettings(options);
    const container = root?.matches?.(settings.containerSelector)
      ? root
      : root?.querySelector?.(settings.containerSelector);
    const list = container?.querySelector?.(settings.trackSelector);
    return { container, list, settings };
  }

  function getMaxScrollLeft(list, settings = getSettings()) {
    if (!list) return 0;
    const items = Array.from(list.querySelectorAll?.(settings.itemSelector) || []);
    const lastItem = items[items.length - 1];
    const styles = window.getComputedStyle ? window.getComputedStyle(list) : null;
    const paddingEnd = Number.parseFloat(styles?.paddingRight || '0') || 0;
    const itemMax = lastItem
      ? lastItem.offsetLeft + lastItem.offsetWidth + paddingEnd - list.clientWidth
      : 0;

    return Math.max(0, list.scrollWidth - list.clientWidth, itemMax);
  }

  function hasScrollableOverflow(list, settings) {
    return getMaxScrollLeft(list, settings) > 1;
  }

  function scrollListTo(list, left, behavior) {
    if (!list) return;
    const nextLeft = Math.max(0, left);

    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left: nextLeft, behavior: getScrollBehavior(behavior) });
      return;
    }

    list.scrollLeft = nextLeft;
  }

  function scrollItemToCenter(list, item, behavior) {
    if (!list || !item) return;
    const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
    scrollListTo(list, itemCenter - (list.clientWidth / 2), behavior);
  }

  function scrollBoundaryItemIntoView(list, item, direction, behavior) {
    if (!list || !item) return;
    const styles = window.getComputedStyle ? window.getComputedStyle(list) : null;
    const paddingEnd = Number.parseFloat(styles?.paddingRight || '0') || 0;
    const paddingStart = Number.parseFloat(styles?.paddingLeft || '0') || 0;
    const nextLeft = direction > 0
      ? Math.max(item.offsetLeft + item.offsetWidth + paddingEnd - list.clientWidth, list.scrollWidth - list.clientWidth)
      : item.offsetLeft - paddingStart;

    scrollListTo(list, nextLeft, behavior);
  }

  function getAdjacentSnapItem(list, direction, settings) {
    const items = Array.from(list?.querySelectorAll?.(settings.itemSelector) || []);
    if (!list || items.length === 0) return null;

    const currentEdge = direction > 0
      ? list.scrollLeft + list.clientWidth
      : list.scrollLeft;
    const tolerance = 2;

    if (direction > 0) {
      return items.find((item) => item.offsetLeft + (item.offsetWidth / 2) > currentEdge - tolerance) || null;
    }

    return items.reverse().find((item) => item.offsetLeft + (item.offsetWidth / 2) < currentEdge + tolerance) || null;
  }

  function isBoundaryItem(list, item, direction, settings) {
    const items = Array.from(list?.querySelectorAll?.(settings.itemSelector) || []);
    if (!item || items.length === 0) return false;
    return direction > 0 ? item === items[items.length - 1] : item === items[0];
  }

  function syncControls(root, options = {}) {
    const { container, list, settings } = getParts(root, options);
    if (!container || !list) return;

    const maxScrollLeft = getMaxScrollLeft(list, settings);
    const hasOverflow = hasScrollableOverflow(list, settings);
    const atStart = list.scrollLeft <= 1;
    const atEnd = list.scrollLeft >= maxScrollLeft - 1;

    container.querySelectorAll(settings.controlSelector).forEach((button) => {
      const isPrev = button.dataset[settings.directionAttribute] === 'prev';
      button.disabled = hasOverflow && (isPrev ? atStart : atEnd);
    });
  }

  function scrollAdjacent(root, direction, options = {}) {
    const { list, settings } = getParts(root, options);
    if (!list) return;

    const target = getAdjacentSnapItem(list, direction, settings);
    if (target && isBoundaryItem(list, target, direction, settings)) {
      scrollBoundaryItemIntoView(list, target, direction, options.behavior);
    } else if (target) {
      scrollItemToCenter(list, target, options.behavior);
    } else {
      scrollListTo(list, list.scrollLeft + (list.clientWidth * 0.75 * direction), options.behavior);
    }

    window.setTimeout(() => syncControls(root, options), 220);
  }

  function centerActive(root, options = {}) {
    const { list, settings } = getParts(root, options);
    const activeItem = list?.querySelector?.(settings.activeSelector);
    if (!activeItem) return;
    scrollItemToCenter(list, activeItem, options.behavior);
    window.setTimeout(() => syncControls(root, options), 220);
  }

  function attach(root, options = {}) {
    const { container, list, settings } = getParts(root, options);
    if (!container || !list) return null;

    const existing = attachedRoots.get(container);
    if (existing) {
      existing.syncControls();
      return existing;
    }

    function handleClick(event) {
      const button = event.target.closest(settings.controlSelector);
      if (!button || !container.contains(button)) return;

      event.preventDefault();
      const direction = button.dataset[settings.directionAttribute] === 'next' ? 1 : -1;
      scrollAdjacent(container, direction, options);
    }

    function handleScroll() {
      syncControls(container, options);
    }

    function handleResize() {
      syncControls(container, options);
    }

    container.addEventListener('click', handleClick);
    list.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame?.(() => syncControls(container, options));
    window.setTimeout(() => syncControls(container, options), 120);

    const controller = {
      centerActive: (centerOptions = {}) => centerActive(container, { ...options, ...centerOptions }),
      syncControls: () => syncControls(container, options),
      destroy() {
        container.removeEventListener('click', handleClick);
        list.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
        attachedRoots.delete(container);
      },
    };

    attachedRoots.set(container, controller);
    return controller;
  }

  window.Express.productBuilderNavScroll = {
    attach,
    centerActive,
    syncControls,
  };
})();
